import { type UUID } from '~/types/common';

import { filter, map, pipe, unique } from '@vestigas/utils';

import { type SGW } from '../../useQueryDeliveryNoteChainsAdmin';
import { type DeliveryNoteAdmin } from '../types';

type EntityType = 'costCenter' | 'site';

/**
 * Maps entity types to their corresponding SGW type codes.
 */
const entityTypeToSgwType: Record<EntityType, SGW['type']> = {
  costCenter: 'cc',
  site: 'pr',
} as const satisfies Record<EntityType, SGW['type']>;

/**
 * Generic function to extract IDs of a specific entity type from a delivery note.
 * @param deliveryNote - The delivery note to extract the permitted entity IDs from.
 * @param entityType - The entity type to filter by ('costCenter' or 'site').
 * @returns An array of entity IDs.
 */
export const getPermittedEntityIds = (
  deliveryNote: DeliveryNoteAdmin,
  entityType: EntityType,
): UUID[] => {
  if (!deliveryNote?.sgw?.length) {
    return [];
  }

  const sgwType = entityTypeToSgwType[entityType];

  const entityIds = pipe(
    deliveryNote?.sgw,
    filter(({ type }) => type === sgwType),
    map(({ typeId }) => typeId),
    filter(Boolean),
    unique,
  );

  return entityIds;
};

/**
 * Extracts the IDs of the cost centers that are permitted to the delivery note.
 * @param deliveryNote - The delivery note to extract the permitted cost centers from.
 * @returns An array of cost center IDs.
 */
export const getPermittedCostCenterIds = (
  deliveryNote: DeliveryNoteAdmin,
): UUID[] => {
  return getPermittedEntityIds(deliveryNote, 'costCenter');
};

/**
 * Extracts the IDs of the sites that are permitted to the delivery note.
 * @param deliveryNote - The delivery note to extract the permitted sites from.
 * @returns An array of site IDs.
 */
export const getPermittedSiteIds = (
  deliveryNote: DeliveryNoteAdmin,
): UUID[] => {
  return getPermittedEntityIds(deliveryNote, 'site');
};
